/**
 * PartnerProfile — iter98
 * Edit organization profile + change password.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, Lock } from 'lucide-react';
import PartnerAccountLayout from '../../../components/PartnerAccountLayout';
import { US_STATES } from '../../../utils/usStates';
import { formatUSPhone } from '../../../utils/addressValidation';
import { API, authHeaders } from './_utils';

const inputBase =
  'w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-white/45 text-[14px] focus:outline-none focus:border-gold-500/50 transition-colors';
const inputError =
  'w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-red-500/60 text-white placeholder-white/45 text-[14px] focus:outline-none focus:border-red-500 transition-colors';
const labelCls = 'block text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold mb-2';

function Field({ label, error, required, children }) {
  return (
    <label className="block">
      <span className={labelCls}>{label} {required && <span className="text-gold-400">*</span>}</span>
      {children}
      {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
    </label>
  );
}

export default function PartnerProfile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    organization_name: '', contact_name: '', email: '', phone: '',
    website: '', address: '', city: '', state: '', zip_code: '',
  });

  const [pwd, setPwd] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdErrors, setPwdErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/partners/me`, { headers: authHeaders() });
        const p = r.data.partner;
        setForm({
          organization_name: p.organization_name || '',
          contact_name: p.contact_name || '',
          email: p.email || '',
          phone: p.phone || '',
          website: p.website || '',
          address: p.address || '',
          city: p.city || '',
          state: p.state || '',
          zip_code: p.zip_code || '',
        });
      } catch {
        toast.error('Could not load profile');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  // ZIP autofill
  const onZipBlur = async () => {
    const z = (form.zip_code || '').trim();
    if (!/^\d{5}$/.test(z)) return;
    try {
      const r = await axios.get(`${API}/public/zip-lookup/${z}`);
      if (r.data?.valid) {
        setForm((f) => ({
          ...f,
          city: f.city || r.data.city || '',
          state: f.state || (r.data.state || '').toUpperCase(),
        }));
      }
    } catch {/* soft */}
  };

  const validate = () => {
    const e = {};
    if (!form.organization_name.trim()) e.organization_name = 'Required';
    if (!form.contact_name.trim()) e.contact_name = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    if (form.zip_code && !/^\d{5}$/.test(form.zip_code.trim())) e.zip_code = 'Must be 5 digits';
    if (form.state && form.state.length !== 2) e.state = 'Invalid state';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/partners/me`, form, { headers: authHeaders() });
      toast.success('Profile updated');
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d === 'email_already_registered') setErrors((e) => ({ ...e, email: 'Email already in use' }));
      else toast.error(typeof d === 'string' ? d : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (ev) => {
    ev.preventDefault();
    const e = {};
    if (!pwd.current_password) e.current_password = 'Required';
    if (!pwd.new_password || pwd.new_password.length < 8) e.new_password = 'Min 8 characters';
    if (pwd.new_password !== pwd.confirm) e.confirm = 'Passwords do not match';
    setPwdErrors(e);
    if (Object.keys(e).length) return;
    setPwdSaving(true);
    try {
      await axios.put(`${API}/partners/me/password`, {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      }, { headers: authHeaders() });
      toast.success('Password updated');
      setPwd({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not update password');
    } finally {
      setPwdSaving(false);
    }
  };

  if (loading) {
    return (
      <PartnerAccountLayout currentKey="profile" title="My Profile">
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-gold-300" /></div>
      </PartnerAccountLayout>
    );
  }

  return (
    <PartnerAccountLayout
      currentKey="profile"
      title="My Profile"
      subtitle="Manage your organization information and login security."
    >
      <form onSubmit={save} className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 lg:p-8 mb-8" data-testid="partner-profile-form">
        <h2 className="text-lg font-bold text-white mb-1">Organization Information</h2>
        <p className="text-xs text-white/45 mb-6">Used in your partner profile, communications, and invoices.</p>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Organization Name" required error={errors.organization_name}>
            <input value={form.organization_name} onChange={(e) => update('organization_name', e.target.value)} className={errors.organization_name ? inputError : inputBase} data-testid="partner-profile-org-name" />
          </Field>
          <Field label="Contact Person" required error={errors.contact_name}>
            <input value={form.contact_name} onChange={(e) => update('contact_name', e.target.value)} className={errors.contact_name ? inputError : inputBase} data-testid="partner-profile-contact" />
          </Field>
          <Field label="Email" required error={errors.email}>
            <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className={errors.email ? inputError : inputBase} data-testid="partner-profile-email" />
          </Field>
          <Field label="Phone Number" error={errors.phone}>
            <input
              value={form.phone}
              onChange={(e) => update('phone', formatUSPhone(e.target.value))}
              placeholder="(617) 555-0123"
              className={errors.phone ? inputError : inputBase}
              data-testid="partner-profile-phone"
            />
          </Field>
          <Field label="Website" error={errors.website}>
            <input value={form.website} onChange={(e) => update('website', e.target.value)} placeholder="https://your-org.org" className={errors.website ? inputError : inputBase} data-testid="partner-profile-website" />
          </Field>
        </div>

        <h3 className="text-sm font-bold text-white mt-8 mb-4 uppercase tracking-[0.18em] text-white/55">Headquarters Address</h3>
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <Field label="Street Address" error={errors.address}>
              <input value={form.address} onChange={(e) => update('address', e.target.value)} placeholder="123 Main Street, Suite 200" className={errors.address ? inputError : inputBase} data-testid="partner-profile-address" />
            </Field>
          </div>
          <Field label="ZIP Code" error={errors.zip_code}>
            <input value={form.zip_code} onChange={(e) => update('zip_code', e.target.value.replace(/\D/g, '').slice(0, 5))} onBlur={onZipBlur} placeholder="02118" inputMode="numeric" className={errors.zip_code ? inputError : inputBase} data-testid="partner-profile-zip" />
          </Field>
          <Field label="City" error={errors.city}>
            <input value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Boston" className={errors.city ? inputError : inputBase} data-testid="partner-profile-city" />
          </Field>
          <Field label="State" error={errors.state}>
            <select value={form.state} onChange={(e) => update('state', e.target.value)} className={errors.state ? inputError : inputBase} data-testid="partner-profile-state">
              <option value="" className="bg-zinc-900">Select state…</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code} className="bg-zinc-900">{s.code} — {s.name}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="mt-8 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 disabled:opacity-50 text-black font-bold text-sm whitespace-nowrap transition-all"
            data-testid="partner-profile-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </button>
        </div>
      </form>

      {/* Security / change password */}
      <form onSubmit={savePassword} className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 lg:p-8" data-testid="partner-profile-password-form">
        <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2"><Lock className="w-4 h-4 text-gold-300" /> Change Password</h2>
        <p className="text-xs text-white/45 mb-6">Minimum 8 characters. Choose something unique to your organization.</p>

        <div className="grid sm:grid-cols-3 gap-5">
          <Field label="Current Password" required error={pwdErrors.current_password}>
            <input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className={pwdErrors.current_password ? inputError : inputBase} data-testid="partner-profile-pwd-current" />
          </Field>
          <Field label="New Password" required error={pwdErrors.new_password}>
            <input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className={pwdErrors.new_password ? inputError : inputBase} data-testid="partner-profile-pwd-new" />
          </Field>
          <Field label="Confirm New Password" required error={pwdErrors.confirm}>
            <input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className={pwdErrors.confirm ? inputError : inputBase} data-testid="partner-profile-pwd-confirm" />
          </Field>
        </div>
        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={pwdSaving}
            className="inline-flex items-center gap-2 h-11 px-6 rounded-xl border border-gold-500/30 hover:border-gold-500/60 bg-white/[0.02] text-gold-200 font-bold text-sm whitespace-nowrap transition-all"
            data-testid="partner-profile-pwd-save"
          >
            {pwdSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            Update Password
          </button>
        </div>
      </form>
    </PartnerAccountLayout>
  );
}
